import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Title } from "@yoast/ui-library";

import SubscriptionTable, { SubscriptionProps } from "./SubscriptionsTable";
import SubscriptionsPagination from "./SubscriptionsPagination";
import * as styles from "./styles.module.scss";

/**
 * One section of the overview: a heading, its own page of rows, and its own pagination.
 *
 * The sections are stacked rather than tabbed. `needsAttention` in particular is a call to action — a
 * lapsed subscription the customer can still renew — so it has to be visible without being asked for.
 * Behind a tab, someone whose subscription is on hold sees nothing wrong until they happen to click the
 * right one.
 *
 * Each section pages independently, because one paginated window cannot span sections: a page boundary
 * falling inside a section is exactly the incoherence that made client-side grouping untenable. The cost
 * is one request per section, which is affordable only because each response is a bounded page.
 *
 * The count in the heading comes from the account-wide bucket counts, not from `meta.total` or the rows,
 * so it describes the section rather than the page currently shown.
 *
 * `size="2"` and the `<br />` below match what the table's own heading rendered before the sections took
 * it over. Anything smaller reads as a subheading of the page title rather than as a section of its own,
 * which is a regression in a layout whose whole point is that the sections are peers.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element|null} The section, or null when it holds nothing.
 */
const SubscriptionsSection = ( props ) => {
	// An empty section is absent rather than shown as a zero, matching what the page has always done: each
	// stacked section used to be gated on being non-empty, so a customer with no partner subscriptions
	// never saw that heading.
	if ( props.count === 0 ) {
		return null;
	}

	const headingId = `subscriptions-section-${ props.bucket }`;

	return (
		<section className={ styles.subscriptionsSection } aria-labelledby={ headingId }>
			<Title as="h2" size="2" id={ headingId }>
				<FormattedMessage { ...props.title } />
				<span className={ styles.sectionCount }>{ props.count }</span>
			</Title>
			<br />

			<SubscriptionTable
				subscriptions={ props.subscriptions }
				onManage={ props.onManage }
				needsAttention={ props.bucket === "needsAttention" }
				isProvisioned={ props.bucket === "provisioned" }
				isInactive={ props.bucket === "inactive" }
				showDetailsModal={ props.showDetailsModal }
				hideHeading={ true }
			/>
			<SubscriptionsPagination meta={ props.meta } onNavigate={ props.onPageChange } />
		</section>
	);
};

SubscriptionsSection.propTypes = {
	bucket: PropTypes.oneOf( [ "needsAttention", "provisioned", "regular", "inactive" ] ).isRequired,
	title: PropTypes.object.isRequired,
	count: PropTypes.number.isRequired,
	subscriptions: SubscriptionProps,
	meta: PropTypes.shape( {
		total: PropTypes.number.isRequired,
		page: PropTypes.number.isRequired,
		limit: PropTypes.number.isRequired,
		totalPages: PropTypes.number.isRequired,
	} ).isRequired,
	onPageChange: PropTypes.func.isRequired,
	onManage: PropTypes.func.isRequired,
	showDetailsModal: PropTypes.func,
};

SubscriptionsSection.defaultProps = {
	subscriptions: [],
	showDetailsModal: null,
};

export default SubscriptionsSection;
